import { StreamProcessorSrc } from './worklets/stream_processor.js';
import { AudioAnalysis } from './analysis/audio_analysis.js';
import { RealtimeEventHandler } from '../../realtime-api-beta/lib/event_handler.js';
import { NetworkMonitor } from './network_monitor.js';

/**
 * Plays audio streams received in raw PCM16 chunks from the browser
 * @class
 */
export class WavStreamPlayer extends RealtimeEventHandler {
  /**
   * Creates a new WavStreamPlayer instance
   * @param {{sampleRate?: number, bufferMs?: number, adaptiveBuffering?: boolean}} options
   * @returns {WavStreamPlayer}
   */
  constructor({ sampleRate = 44100, bufferMs = 240, adaptiveBuffering = true } = {}) {
    super(); // Initialize the event handler
    this.scriptSrc = StreamProcessorSrc;
    this.sampleRate = sampleRate;
    this.bufferMs = Math.max(0, Math.min(2000, Number(bufferMs) || 0));
    this.context = null;
    this.stream = null;
    this.analyser = null;
    this.trackSampleOffsets = {};
    this.interruptedTrackIds = {};
    this.volume = 1.0;
    this.adaptiveBuffering = adaptiveBuffering;
    this.networkMonitor = null;
    
    // Initialize network-adaptive buffering if enabled
    if (this.adaptiveBuffering) {
      this.networkMonitor = new NetworkMonitor();
      this.networkMonitor.onSpeedChange((speed) => {
        const recommendedBufferMs = this.networkMonitor.getRecommendedBufferMs();
        this.setBufferMs(recommendedBufferMs);
        console.log(`[WavStreamPlayer] Network speed: ${speed}, buffer adjusted to ${recommendedBufferMs}ms`);
      });
    }
  }

  /**
   * Connects the audio context and enables output to speakers
   * @returns {Promise<true>}
   */
  async connect() {
    // Clean up previous session state so reconnects start fresh
    this.interruptedTrackIds = {};
    this.trackSampleOffsets = {};
    if (this.stream) {
      try { this.stream.disconnect(); } catch (_) {}
      this.stream = null;
    }
    this.context = new AudioContext({ sampleRate: this.sampleRate });

    // CRITICAL: Log actual AudioContext sample rate
    console.log(`[WavStreamPlayer] Requested sample rate: ${this.sampleRate}Hz`);
    console.log(`[WavStreamPlayer] Actual AudioContext sample rate: ${this.context.sampleRate}Hz`);
    
    if (this.context.sampleRate !== this.sampleRate) {
      console.warn(`⚠️ AudioContext sample rate mismatch! Requested ${this.sampleRate}Hz but got ${this.context.sampleRate}Hz`);
      console.warn(`⚠️ This will cause audio to play at wrong speed. Audio will be ${(this.context.sampleRate / this.sampleRate).toFixed(2)}x speed.`);
    }
    
    if (this.context.state === 'suspended') {
      console.log('AudioContext is suspended. It may require user interaction to resume.');
      await this.context.resume();
    }
    try {
      await this.context.audioWorklet.addModule(this.scriptSrc);
    } catch (e) {
      console.error(e);
      throw new Error(`Could not add audioWorklet module: ${this.scriptSrc}`);
    }
    const analyser = this.context.createAnalyser();
    analyser.fftSize = 8192;
    analyser.smoothingTimeConstant = 0.1;
    this.analyser = analyser;
    return true;
  }

  /**
   * Gets the current frequency domain data from the playing track
   * @param {"frequency"|"music"|"voice"} [analysisType]
   * @param {number} [minDecibels] default -100
   * @param {number} [maxDecibels] default -30
   * @returns {import('./analysis/audio_analysis.js').AudioAnalysisOutputType}
   */
  getFrequencies(
    analysisType = 'frequency',
    minDecibels = -100,
    maxDecibels = -30
  ) {
    if (!this.analyser) {
      throw new Error('Not connected, please call .connect() first');
    }
    return AudioAnalysis.getFrequencies(
      this.analyser,
      this.sampleRate,
      null,
      analysisType,
      minDecibels,
      maxDecibels
    );
  }

  /**
   * Starts audio streaming
   * @private
   * @returns {Promise<true>}
   */
   _start() {
    const streamNode = new AudioWorkletNode(this.context, 'stream_processor');
    streamNode.connect(this.context.destination);
    streamNode.port.onmessage = (e) => {
      const { event } = e.data;
      if (event === 'stop') {
        const { trackId } = e.data;
        if (streamNode && streamNode.disconnect) {
          streamNode.disconnect();
        }
        this.stream = null;
        
        // Emit the stop event so consumers can listen for it
        this.emit('track.complete', trackId);
        this.emit('track.activation.complete', trackId);
      } else if (event === 'offset') {
        const { requestId, trackId, offset } = e.data;
        const currentTime = offset / this.sampleRate;
        this.trackSampleOffsets[requestId] = { trackId, offset, currentTime };
      } else if (event === 'buffering_start') {
        const { trackId, bufferLength, sampleOffset } = e.data;
        console.log(
          `[WavStreamPlayer] Buffering started on track ${trackId} ` +
          `(consumed ${sampleOffset}/${bufferLength} samples, waiting for more data)`
        );
        this.emit('buffer.waiting', { trackId, bufferLength, sampleOffset });
      } else if (event === 'buffer_underrun') {
        const { trackId, graceFrames } = e.data;
        console.warn(
          `[WavStreamPlayer] Buffer underrun on track ${trackId} ` +
          `(waited ${graceFrames} frames). Consider increasing buffer size.`
        );
        // Emit event for monitoring
        this.emit('buffer.underrun', { trackId, graceFrames });
      }
    };
    this.analyser.disconnect();
    streamNode.connect(this.analyser);
    this.stream = streamNode;
    
    // Set initial volume
    this.setVolume(this.volume);
    // Configure initial playback buffer target
    try {
      if (typeof this.bufferMs === 'number') {
        this.stream.port.postMessage({ event: 'buffer.ms', value: this.bufferMs });
      }
    } catch (err) {
      // best effort
    }
    
    return true;
  }

  /**
   * Emits an event for listeners
   * @param {string} eventName - Name of the event
   * @param {any} data - Data to pass with the event
   * @private
   */
  emit(eventName, data) {
    const handlers = this.eventHandlers[eventName] || [];
    for (const handler of handlers) {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in event handler for ${eventName}:`, error);
      }
    }
    
    // Also handle one-time listeners
    const nextHandlers = this.nextEventHandlers[eventName] || [];
    if (nextHandlers.length > 0) {
      for (const handler of nextHandlers) {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in one-time event handler for ${eventName}:`, error);
        }
      }
      this.nextEventHandlers[eventName] = [];
    }
  }

  /**
   * Adds 16BitPCM data to the currently playing audio stream
   * You can add chunks beyond the current play point and they will be queued for play
   * @param {ArrayBuffer|Int16Array} arrayBuffer
   * @param {string} [trackId]
   * @returns {Int16Array}
   */
  add16BitPCM(arrayBuffer, trackId = 'default') {
    if (typeof trackId !== 'string') {
      throw new Error(`trackId must be a string`);
    } else if (this.interruptedTrackIds[trackId]) {
      return;
    }
    // Safety resume: AudioContext may still be suspended if the initial
    // resume() in connect() ran outside a user-gesture call stack (e.g.
    // after an awaited getUserMedia). Calling resume() here is harmless
    // if already running and fixes Safari/WebKit where the gesture expires.
    if (this.context && this.context.state === 'suspended') {
      this.context.resume().catch(() => {});
    }
    if (!this.stream) {
      this._start();
    }
    let buffer;
    if (arrayBuffer instanceof Int16Array) {
      buffer = arrayBuffer;
    } else if (arrayBuffer instanceof ArrayBuffer) {
      buffer = new Int16Array(arrayBuffer);
    } else {
      throw new Error(`argument must be Int16Array or ArrayBuffer`);
    }

    // NOTE: Resampling is now handled in OpusDecoder to avoid double resampling
    // OpusDecoder detects actual decoded rate and resamples to target 24kHz
    // WavStreamPlayer just passes through the 24kHz audio to AudioContext
    // If AudioContext is 48kHz, the browser's native resampler handles it better than our linear interpolation

    // Emit event when audio data is added
    this.emit('audio.data', { trackId, buffer });

    // IMPORTANT: Clone the buffer before posting to worklet to prevent
    // ArrayBuffer transfer/detachment issues that cause audio glitches
    // postMessage can transfer ownership, leaving the original buffer empty
    const clonedBuffer = new Int16Array(buffer);
    this.stream.port.postMessage({ event: 'write', buffer: clonedBuffer, trackId });
    return buffer;
  }
  
  /**
   * Notifies the StreamProcessor that a track is complete
   * @param {string} trackId
   */
  markTrackComplete(trackId) {
    if (!this.stream) {
      return;
    }
    this.stream.port.postMessage({
      event: 'track.complete',
      trackId
    });
    this.stream.port.postMessage({
      event: 'track.activation.complete',
      trackId
    });
  }

  /**
   * Gets the offset (sample count) of the currently playing stream
   * @param {boolean} [interrupt]
   * @returns {{trackId: string|null, offset: number, currentTime: number}}
   */
  async getTrackSampleOffset(interrupt = false) {
    if (!this.stream || !this.stream.port) {
      return { trackId: null, offset: 0, currentTime: 0 };
    }
    const requestId = crypto.randomUUID();
    
    try {
      // If this is an interrupt request, add a delay to allow trailing audio to play
      if (interrupt) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // 1-second delay
      }
      
      this.stream.port.postMessage({
        event: interrupt ? 'interrupt' : 'offset',
        requestId,
      });
      
      let trackSampleOffset;
      const startTime = Date.now();
      const timeout = 3000; // 3 second timeout
      
      while (!trackSampleOffset && (Date.now() - startTime) < timeout) {
        trackSampleOffset = this.trackSampleOffsets[requestId];
        await new Promise((r) => setTimeout(() => r(), 10));
      }
      
      if (!trackSampleOffset) {
        console.warn('Timeout waiting for track sample offset');
        return { trackId: null, offset: 0, currentTime: 0 };
      }
      
      const { trackId } = trackSampleOffset;
      if (interrupt && trackId) {
        this.interruptedTrackIds[trackId] = true;
      }
      
      return trackSampleOffset;
    } catch (error) {
      console.error('Error getting track sample offset:', error);
      return { trackId: null, offset: 0, currentTime: 0 };
    }
  }

  /**
   * Interrupts the current playback and returns the current offset
   * @returns {{trackId: string|null, offset: number, currentTime: number}}
   */
  async interrupt() {
    return this.getTrackSampleOffset(true);
  }
  
  /**
   * Sets the volume for audio playback
   * @param {number} volume - Volume level between 0 (mute) and 1 (full volume)
   * @returns {boolean}
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    if (!this.stream) {
      return false;
    }
    // Send volume setting to the audio worklet
    this.stream.port.postMessage({
      event: 'volume',
      volume: this.volume
    });
    return true;
  }

  /**
   * Sets the playback jitter buffer target in milliseconds
   * @param {number} ms
   * @returns {boolean}
   */
  setBufferMs(ms) {
    const clamped = Math.max(0, Math.min(2000, Number(ms) || 0));
    this.bufferMs = clamped;
    if (!this.stream) return false;
    try {
      this.stream.port.postMessage({ event: 'buffer.ms', value: this.bufferMs });
      return true;
    } catch (err) {
      return false;
    }
  }

  /**
   * Mutes the audio without stopping playback
   * @returns {boolean}
   */
  mute() {
    return this.setVolume(0);
  }

  /**
   * Unmutes the audio, restoring previous volume
   * @returns {boolean}
   */
  unmute() {
    return this.setVolume(1);
  }

  /**
   * Get network information for debugging
   * @returns {object|null} Network info or null if adaptive buffering disabled
   */
  getNetworkInfo() {
    if (!this.networkMonitor) {
      return null;
    }
    return this.networkMonitor.getNetworkInfo();
  }

  /**
   * Get current buffer status
   * @returns {object} Buffer status
   */
  getBufferStatus() {
    return {
      bufferMs: this.bufferMs,
      adaptiveBuffering: this.adaptiveBuffering,
      networkSpeed: this.networkMonitor ? this.networkMonitor.getCurrentSpeed() : 'unknown',
      networkInfo: this.getNetworkInfo()
    };
  }

  /**
   * Cleanup resources
   */
  destroy() {
    if (this.networkMonitor) {
      this.networkMonitor.destroy();
      this.networkMonitor = null;
    }
    
    if (this.stream) {
      try {
        this.stream.disconnect();
      } catch (e) {
        // ignore
      }
      this.stream = null;
    }
    
    if (this.context) {
      try {
        this.context.close();
      } catch (e) {
        // ignore
      }
      this.context = null;
    }
  }
}

// eslint-disable-next-line no-undef
globalThis.WavStreamPlayer = WavStreamPlayer;